import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { LoteAsignacion } from '../../services/loteService';
import LoteAdjuntosPanel from './LoteAdjuntosPanel';
import type { LoteMode } from '../../utils/loteLineaHelpers';

export interface LoteDetalleReadonlyProps {
  codigoArticulo?: string | null;
  cantidad: number;
  nroUbicacion?: number | null;
  mode?: LoteMode;
  lotes?: LoteAsignacion[] | null;
  /** Compacto para tablas. */
  dense?: boolean;
}

const formatQty = (n: number) =>
  Number(n).toLocaleString('es-AR', { maximumFractionDigits: 4 });

/**
 * Muestra lotes de un renglón ya grabado y abre un popup de consulta
 * con certificados (solo descarga).
 */
const LoteDetalleReadonly: React.FC<LoteDetalleReadonlyProps> = ({
  codigoArticulo,
  cantidad,
  lotes,
  dense,
}) => {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(lotes) ? lotes.filter((l) => (Number(l.cantidad) || 0) > 0) : [];
  const codigo = String(codigoArticulo ?? '').trim() || '—';
  const suma = list.reduce((acc, l) => acc + (Number(l.cantidad) || 0), 0);

  if (list.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const openPopup = () => setOpen(true);

  return (
    <>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
        {list.map((l, idx) => {
          const label = `${l.nroLote || '—'}${list.length > 1 ? ` (${formatQty(l.cantidad)})` : ''}`;
          return (
            <Chip
              key={`${l.idLote ?? l.nroLote}-${idx}`}
              size="small"
              label={label}
              variant="outlined"
              color="primary"
              clickable
              onClick={openPopup}
              title="Ver lotes y certificados"
              sx={{ height: dense ? 22 : 24, maxWidth: 220 }}
            />
          );
        })}
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Lotes — {codigo}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Cantidad del renglón: {formatQty(cantidad)} · Asignado: {formatQty(suma)}
          </Typography>

          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Lote / Serie</TableCell>
                <TableCell>Vencimiento</TableCell>
                <TableCell align="right">Cantidad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((l, idx) => (
                <TableRow key={`${l.idLote ?? l.nroLote}-${idx}`}>
                  <TableCell>{l.nroLote || '—'}</TableCell>
                  <TableCell>
                    {l.fechaVencimiento ? String(l.fechaVencimiento).slice(0, 10) : '—'}
                  </TableCell>
                  <TableCell align="right">{formatQty(l.cantidad)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Certificados / documentos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Solo descarga. Para cargar o eliminar archivos usá la ficha del lote en Inventario.
          </Typography>

          <Stack spacing={2} divider={<Divider flexItem />}>
            {list.map((l, idx) => {
              const idLote = l.idLote != null && Number(l.idLote) > 0 ? Number(l.idLote) : null;
              return (
                <Box key={`${idLote ?? l.nroLote}-${idx}`}>
                  {idLote == null ? (
                    <Typography variant="body2" color="text.secondary">
                      {l.nroLote || 'Lote'}: sin identificador para consultar documentos.
                    </Typography>
                  ) : (
                    <LoteAdjuntosPanel
                      idLote={idLote}
                      downloadOnly
                      title={`Documentos — ${l.nroLote || `Lote ${idLote}`}`}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoteDetalleReadonly;
